import React, { useCallback, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import withStyles from 'react-jss';
import { Table, Popconfirm, Tooltip } from 'antd';
import { useSelector, useDispatch } from 'react-redux';
import { Link } from 'react-router-dom';
import { EditOutlined, DeleteOutlined } from '@ant-design/icons';
import moment from 'moment';

import styles from '../styles';
import { getAccessories, clearAccessories } from '../Actions';
import { errorNotification, successNotification } from '../Snackbar';
import {
  sorter,
  onFilter,
  filterDropdown,
  filterIcon,
  get,
  dateSorter,
  onDateFilter,
  dateFilterDropdown,
} from '../utils';
import Api from '../Api';
import constants from '../constants';
import Breadcrumbs from '../Components/Breadcrumbs';

const AccessoryList = props => {
  const { classes } = props;
  const dispatch = useDispatch();

  const auth = useSelector(state => state.auth);
  const permissions = useMemo(() => get(auth, ['permissions'], []), [auth]);
  const accessories = useSelector(state => state.accessories);

  useEffect(() => {
    dispatch(getAccessories());
    return () => {
      dispatch(clearAccessories());
    };
  }, []);

  const onDelete = useCallback(async id => {
    try {
      await Api.delete(`/accessories/${id}`);
      successNotification('Accessory deleted');
      dispatch(getAccessories());
    } catch (err) {
      errorNotification(err);
    }
  }, []);

  const updateColumn = useMemo(
    () => record =>
      (
        <>
          {permissions.includes(constants.permissions.updateAccessory) ? (
            <Tooltip placement="bottom" title="Update">
              <Link to={`/accessories/update/${record.id}`}>
                <EditOutlined className={classes.tableIcon} />
              </Link>
            </Tooltip>
          ) : null}
        </>
      ),
    []
  );

  const deleteColumn = useMemo(
    () => record =>
      (
        <>
          {permissions.includes(constants.permissions.deleteAccessory) ? (
            <Tooltip placement="bottom" title="Delete">
              <Popconfirm
                title="Are you sure you want to delete?"
                onConfirm={() => onDelete(record.id)}
                okText="Yes"
                cancelText="No"
              >
                <DeleteOutlined className={classes.tableIcon} />
              </Popconfirm>
            </Tooltip>
          ) : null}
        </>
      ),
    []
  );

  const columns = useMemo(() => {
    const column = [
      {
        title: 'Accessory',
        dataIndex: 'accessory',
        sorter: sorter('accessory'),
        onFilter: onFilter('accessory'),
        filterDropdown,
        filterIcon,
        width: '15%',
      },
      {
        title: 'Name',
        dataIndex: 'name',
        sorter: sorter('name'),
        onFilter: onFilter('name'),
        filterDropdown,
        filterIcon,
      },
      {
        title: 'GSM',
        dataIndex: 'gsm',
        sorter: sorter('gsm'),
        onFilter: onFilter('gsm'),
        filterDropdown,
        filterIcon,
      },
      {
        title: 'Size',
        dataIndex: 'size',
        sorter: sorter('size'),
        onFilter: onFilter('size'),
        filterDropdown,
        filterIcon,
      },
      {
        title: 'Unit',
        dataIndex: 'unit',
        sorter: sorter('unit'),
        onFilter: onFilter('unit'),
        filterDropdown,
        filterIcon,
      },
      {
        title: 'Quality',
        dataIndex: 'quality',
        sorter: sorter('quality'),
        onFilter: onFilter('quality'),
        filterDropdown,
        filterIcon,
      },
      {
        title: 'Color',
        dataIndex: 'color',
        sorter: sorter('color'),
        onFilter: onFilter('color'),
        filterDropdown,
        filterIcon,
      },
      {
        title: 'Shade',
        dataIndex: 'shade',
        sorter: sorter('shade'),
        onFilter: onFilter('shade'),
        filterDropdown,
        filterIcon,
      },
      {
        title: 'Remark',
        dataIndex: 'remark',
        sorter: sorter('remark'),
        onFilter: onFilter('remark'),
        filterDropdown,
        filterIcon,
      },
    ];

    if (permissions.includes(constants.permissions.updateAccessory)) {
      column.push({
        title: 'Update',
        render: updateColumn,
      });
    }

    if (permissions.includes(constants.permissions.deleteAccessory)) {
      column.push({
        title: 'Delete',
        render: deleteColumn,
      });
    }

    column.push(
      {
        title: 'Created',
        children: [
          {
            title: 'By',
            dataIndex: 'createdBy',
            sorter: sorter('createdBy'),
            onFilter: onFilter('createdBy'),
            filterDropdown,
            filterIcon,
          },
          {
            title: 'Date',
            dataIndex: 'createdAt',
            sorter: dateSorter('createdAt'),
            onFilter: onDateFilter('createdAt'),
            filterDropdown: dateFilterDropdown,
            filterIcon,
            render: createdAt =>
              createdAt ? moment(createdAt).format('DD MMM YYYY') : null,
          },
        ],
      },
      {
        title: 'Last Updated',
        children: [
          {
            title: 'By',
            dataIndex: 'updatedBy',
            sorter: sorter('updatedBy'),
            onFilter: onFilter('updatedBy'),
            filterDropdown,
            filterIcon,
          },
          {
            title: 'Date',
            dataIndex: 'updatedAt',
            sorter: dateSorter('updatedAt'),
            onFilter: onDateFilter('updatedAt'),
            filterDropdown: dateFilterDropdown,
            filterIcon,
            render: updatedAt =>
              updatedAt ? moment(updatedAt).format('DD MMM YYYY') : null,
          },
        ],
      }
    );

    return column;
  }, []);

  const rowKey = useMemo(() => record => record.id, []);

  const pagination = useMemo(
    () => ({
      defaultPageSize: 20,
      position: ['bottomLeft'],
      showTotal: total => `Total ${total} Records`,
    }),
    []
  );

  const scroll = useMemo(() => ({ x: 1600, y: window.innerHeight - 300 }), []);

  return (
    <>
      <Breadcrumbs
        moduleName="Accessory"
        moduleLink="/accessories/list"
        subModuleName="List"
      />
      <div className={classes.tableStyle}>
        <Table
          bordered
          size="small"
          columns={columns}
          dataSource={accessories}
          rowKey={rowKey}
          pagination={pagination}
          scroll={scroll}
        />
      </div>
    </>
  );
};

AccessoryList.propTypes = {
  classes: PropTypes.object.isRequired,
};
export default withStyles(styles)(AccessoryList);
